/**
 * 项目发布列表
 * 展示已发布到当前 App 的项目
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Loader2,
  Rocket,
  Trash2,
  ExternalLink,
  Crown,
  Star,
  Search,
} from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Badge } from '@/shared/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { useToast } from '@/shared/hooks/useToast'
import { ProjectPublishApiService, CategoryApiService } from '../services/app-api'
import type { PublishedProject, Category } from '../types/app'
import ProjectPublishDialog from './ProjectPublishDialog'

interface ProjectPublishListProps {
  appId: string
}

// 订阅等级显示配置
const TIER_CONFIG = {
  free: { label: '免费', variant: 'secondary' as const },
  tier1: { label: '基础版', variant: 'default' as const },
  tier2: { label: '高级版', variant: 'destructive' as const },
}

export default function ProjectPublishList({ appId }: ProjectPublishListProps) {
  const navigate = useNavigate()
  const { showSuccess, showError } = useToast()

  // 状态
  const [projects, setProjects] = useState<PublishedProject[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false)
  const [projectToUnpublish, setProjectToUnpublish] = useState<PublishedProject | null>(null)
  const [isUnpublishing, setIsUnpublishing] = useState(false)
  
  // 筛选状态
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('__all__')
  
  // 分页状态
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 20

  // 加载项目列表
  const loadProjects = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await ProjectPublishApiService.getAppProjects(appId, {
        category: categoryFilter === '__all__' ? undefined : categoryFilter,
        search: searchQuery.trim() || undefined,
        page,
        page_size: pageSize,
      })
      if (response.success && response.data) {
        setProjects(response.data.projects)
        setTotal(response.data.total)
        setTotalPages(response.data.total_pages)
      }
    } catch (error) {
      console.error('❌ [ProjectPublishList] 加载项目列表失败:', error)
      showError('加载项目列表失败')
    } finally {
      setIsLoading(false)
    }
  }, [appId, categoryFilter, searchQuery, page, showError])

  // 加载分类列表
  const loadCategories = useCallback(async () => {
    try {
      const response = await CategoryApiService.getCategories(appId)
      if (response.success && response.data) {
        setCategories(response.data.categories)
      }
    } catch (error) {
      console.error('❌ [ProjectPublishList] 加载分类列表失败:', error)
    }
  }, [appId])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  // 搜索延迟
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1)
      loadProjects()
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // 筛选变化时重置页码
  useEffect(() => {
    setPage(1)
  }, [categoryFilter])

  // 发布成功回调
  const handlePublishSuccess = () => {
    setIsPublishDialogOpen(false)
    loadProjects()
  }

  // 取消发布
  const handleUnpublish = async () => {
    if (!projectToUnpublish) return

    setIsUnpublishing(true)
    try {
      // v11.2.2: 需要传入 appId 指定取消发布的目标 App
      const response = await ProjectPublishApiService.unpublishProject(
        projectToUnpublish.project_id,
        appId
      )
      if (response.success) {
        showSuccess(`项目「${projectToUnpublish.name}」已取消发布`)
        setProjects((prev) =>
          prev.filter((p) => p.project_id !== projectToUnpublish.project_id)
        )
        setTotal((prev) => prev - 1)
        setProjectToUnpublish(null)
      }
    } catch (error) {
      console.error('❌ [ProjectPublishList] 取消发布失败:', error)
      showError('取消发布失败')
    } finally {
      setIsUnpublishing(false)
    }
  }

  // 获取分类名称
  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return '未分类'
    const category = categories.find((c) => c.category_id === categoryId)
    return category?.name || categoryId
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>已发布项目</CardTitle>
          <CardDescription>
            管理已发布到此 App 的项目，共 {total} 个
          </CardDescription>
        </div>
        <Button onClick={() => setIsPublishDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          发布项目
        </Button>
      </CardHeader>
      <CardContent>
        {/* 筛选栏 */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="搜索项目名称..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="筛选分类" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">全部分类</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.category_id} value={category.category_id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 项目列表 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
              <Rocket className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
              {searchQuery || categoryFilter !== '__all__'
                ? '没有找到匹配的项目'
                : '暂无已发布项目'}
            </h3>
            <p className="text-slate-500 mb-4 max-w-sm">
              {searchQuery || categoryFilter !== '__all__'
                ? '尝试调整筛选条件'
                : '发布项目到此 App，让用户可以访问和使用。'}
            </p>
            {!searchQuery && categoryFilter === '__all__' && (
              <Button onClick={() => setIsPublishDialogOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                发布第一个项目
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {projects.map((project) => (
                <div
                  key={project.project_id}
                  className="flex items-center gap-4 p-4 border rounded-lg bg-white dark:bg-slate-900 hover:shadow-sm transition-shadow"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium truncate">{project.name}</span>
                      {project.is_recommended && (
                        <Badge variant="outline" className="gap-1 text-amber-600 border-amber-300">
                          <Star className="h-3 w-3" />
                          推荐
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate">
                      {project.description || '暂无描述'}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span>分类：{getCategoryName(project.category)}</span>
                      {project.tags && project.tags.length > 0 && (
                        <span>标签：{project.tags.join('、')}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Badge
                      variant={
                        TIER_CONFIG[project.subscription_tier as keyof typeof TIER_CONFIG]
                          ?.variant || 'secondary'
                      }
                      className="gap-1"
                    >
                      <Crown className="h-3 w-3" />
                      {TIER_CONFIG[project.subscription_tier as keyof typeof TIER_CONFIG]
                        ?.label || project.subscription_tier}
                    </Badge>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/project/${project.project_id}`)}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => setProjectToUnpublish(project)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  上一页
                </Button>
                <span className="text-sm text-slate-500">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  下一页
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* 发布项目弹窗 */}
      <ProjectPublishDialog
        open={isPublishDialogOpen}
        onOpenChange={setIsPublishDialogOpen}
        appId={appId}
        categories={categories}
        onSuccess={handlePublishSuccess}
      />

      {/* 取消发布确认弹窗 */}
      <AlertDialog
        open={!!projectToUnpublish}
        onOpenChange={(open) => !open && setProjectToUnpublish(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>取消发布</AlertDialogTitle>
            <AlertDialogDescription>
              确定要取消发布项目「{projectToUnpublish?.name}」吗？取消后，用户将无法在 App 中看到此项目。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnpublishing}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnpublish}
              disabled={isUnpublishing}
              className="bg-red-600 hover:bg-red-700"
            >
              {isUnpublishing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  处理中...
                </>
              ) : (
                '确认取消发布'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
